import { Component, inject } from '@angular/core';
import { CountryCodeFieldComponent } from '../../country-code-field.component';
import {
  ButtonClickEvent,
  CacBaseDialogComponent,
  CacButtonComponent,
  CacDialogLayoutComponent,
  CacFieldComponent,
  CacTabComponent,
  CacTableComponent,
  CacTabsComponent,
  formBuilder,
  table,
} from '@controladad/ng-base';
import { UserEntity, VendorInfo } from '@models';
import { AddressApiService, UserApiService, OrderApiService } from '../../../services';
import { HouseTypeValues, Roles, StatusValues } from '../../../data';
import { formControl, formGroup, Validators } from '@al00x/forms';
import { NgIf } from '@angular/common';
import { cacTableIdCol } from '../../table';
import { switchMap, of } from 'rxjs';
import { KwdCurrencyPipe } from '../../../pipes';
import { AuthStore } from '../../../states';

export interface CustomerUserDialogData {
  user?: UserEntity;
}

export type CustomerUserDialogResult = boolean;

@Component({
  selector: 'lib-customer-dialog',
  standalone: true,
  imports: [
    CacDialogLayoutComponent,
    CacTableComponent,
    CacFieldComponent,
    CountryCodeFieldComponent,
    CacButtonComponent,
    CacTabsComponent,
    CacTabComponent,
    NgIf,
  ],
  templateUrl: './customer-user-dialog.component.html',
  styleUrl: './customer-user-dialog.component.scss',
})
export class CustomerUserDialogComponent extends CacBaseDialogComponent<
  CustomerUserDialogData,
  CustomerUserDialogResult
> {
  private userApi = inject(UserApiService);
  private addressApi = inject(AddressApiService);
  private orderApi = inject(OrderApiService);
  private kwdCurrencyPipe = new KwdCurrencyPipe();
  readonly isRestrictedAdmin = inject(AuthStore).isRestrictedAdmin();

  form = formGroup({
    fullName: formControl<string>(undefined, [Validators.required, Validators.maxLength(257)]),
    status: formControl<number>(1, Validators.required),
    email: formControl<string>(undefined, [Validators.required, Validators.email]),
    countryCode: formControl<string | null>(this.data.user ? null : '+965'),
    phoneNumber: formControl<string>(undefined, [Validators.required, Validators.pattern(/^[+\d\s()-]{6,32}$/)]),
  });

  StatusValues = StatusValues;

  constructor() {
    super();
    const user = this.data.user;
    if (user) {
      this.form.controls.email.setReadonly(true);

      this.addressTable.options?.actions?.push({
        type: 'icon',
        content: 'delete',
        action: (item) => dialog$.deletePrompt(false, { itemName: 'Address' }).action(() => this.addressApi.delete(user.id, item.id)),
      });
      this.userApi.getCustomerDetails(user?.id).subscribe((r) => {
        this.form.patchValue(r.customerDetailsDTO);
        this.form.controls.phoneNumber.patchValue(r.customerDetailsDTO.phoneNumber);
      });
    }
  }

  addressTable = table({
    itemsFn: () => this.addressApi.getAll(this.data.user?.id),
    selectable: true,
    pagination: false,
    view: {
      itemName: 'Address',
      title: 'Addresses',
    },
    columns: {
      id: { label: 'ID' },
      'governorate.name.english': { label: 'Governorate' },
      'area.name.english': { label: 'Area' },
      address: { label: 'Address' },
    },

    actions: [
      {
        type: 'icon',
        content: 'edit',
        action: (item) => dialog$.customerAddress({ address: item, userId: this.data.user?.id }).afterSubmit(),
      },
    ],

    events: {
      add: () => dialog$.customerAddress({ userId: this.data.user?.id }).afterSubmit(),
    },
  });

  ordersTable = table({
    itemsFn: this.data.user?.id ? this.orderApi.getAdapter({
      default: {
        filters: [
          { key: 'UserId', value: this.data.user.id }
        ]
      }
    }) : () => of([]),
    view: {
      title: 'Orders',
    },
    columns: {
      id: cacTableIdCol(),
      purchaseDate: {
        label: 'Order Date',
        sortable: true,
        filterable: [
          { type: 'greater', label: 'From', controlType: 'date', key: 'PurchaseDateFrom' },
          { type: 'lower', label: 'To', controlType: 'date', key: 'PurchaseDateTo' },
        ],
        type: 'datetime',
      },
      customerFullName: { label: 'Ship To Name', sortable: true, filterable: true },
      totalPrice: {
        label: 'Total Amount', sortable: true, filterable: true,
        transform: (value: number) => this.kwdCurrencyPipe.transform(value),
      },
    },
    // NOT STATED IN THE TICKET
    // BUT PROBABLY NEEDS TO BE IMPLEMENTED >>
    // actions: [
    //   {
    //     type: 'icon',
    //     content: 'view',
    //     action: (item) => dialog$.customerUser({ user: item }).afterSubmit(),
    //   },
    // ],
  });

  onSubmit(e: ButtonClickEvent) {
    const values = this.form.getRawValue();
    (this.data.user
      ? this.userApi.updateCustomer({ ...values, id: this.data.user.id })
      : this.userApi.createCustomer(values)
    )
      .pipe(e.pipe())
      .subscribe(() => {
        this.submit(true);
      });
  }

  onDelete(e: ButtonClickEvent) {
    const id = this.data.user?.id;
    if (!id) return;
    this.userApi
      .delete({
        userIds: id,
      })
      .pipe(e.pipe())
      .subscribe(() => {
        this.submit(true);
        snackbar$.show('Delete Successful');
      });
  }

  onForceLogout(e: ButtonClickEvent) {
    if (this.data.user) {
      this.userApi
        .forceLogout(this.data.user.id, Roles.customer)
        .pipe(e.pipe())
        .subscribe(() => {
          this.dialog.close();
          snackbar$.show('Force Login Successful');
        });
    }
  }

  onWalletTransaction(e: ButtonClickEvent) {
    if (this.data.user) {
      e.setLoading(true);
      dialog$.walletTransaction({ customer: this.data.user }).afterClosed().subscribe(() => { e.setLoading(false) });
    }
  }

  showVendorInfoDialog() {
    return dialog$.input<VendorInfo, any>({
      title: 'Vendor Info',
      formBuilder: formBuilder({
        cols: 1,
        inputs: {
          vendorName: {
            control: formControl<string>(undefined, Validators.required),
            label: 'Vendor Name',
          },
          status: {
            control: formControl<number>(1, Validators.required),
            label: 'Status',
            items: StatusValues,
            type: 'select',
          },
        },
      }),
    });
  }

  addVendorRole(e: ButtonClickEvent) {
    const id = this.data.user?.id;

    if (!id) return;

    return this.showVendorInfoDialog()
      .afterSubmit()
      .pipe(
        e.pipe(),
        switchMap((value) => this.userApi.createVendorFromUser(id, value)),
      )
      .subscribe(() => {
        this.dialog.close();
        snackbar$.show('This Customer is a vendor now');
      });
  }
}
